import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/App";
import { useAuth } from "@/contexts/AuthContext";

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`text-sm uppercase tracking-[0.18em] transition-colors ${
        pathname === to ? "text-white" : "text-zinc-400 hover:text-white"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-baseline gap-3"
        >
          <span className="font-display text-xl font-semibold tracking-tight">
            Stageletics
          </span>
          <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-zinc-500">
            Engineered for Showtime
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {navLink("/product", "Product", "nav-product")}
          {navLink("/wholesale", "Bulk", "nav-wholesale")}
          {navLink("/affiliate", "Affiliate", "nav-affiliate")}
          {navLink("/faq", "FAQ", "nav-faq")}
          {navLink("/returns", "Returns", "nav-returns")}
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="hidden md:flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-zinc-400 hover:text-white transition-colors"
            >
              <User className="w-4 h-4" />
              <span>{user.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="hidden md:inline text-sm uppercase tracking-[0.15em] text-zinc-400 hover:text-white transition-colors"
            >
              Sign in
            </Link>
          )}
          <Link
            to="/cart"
            data-testid="cart-icon-link"
            className="relative p-2 hover:bg-white/5 transition-colors"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-[#E8A851] text-black text-xs font-semibold w-5 h-5 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setOpen((s) => !s)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLink("/product", "Product", "nav-product-mobile")}
            {navLink("/wholesale", "Bulk", "nav-wholesale-mobile")}
            {navLink("/affiliate", "Affiliate", "nav-affiliate-mobile")}
            {navLink("/faq", "FAQ", "nav-faq-mobile")}
            {navLink("/returns", "Returns", "nav-returns-mobile")}
            <div className="pt-4 border-t border-white/10">
              {user ? (
                <div className="flex items-center justify-between gap-4">
                  <Link
                    to="/account"
                    data-testid="nav-account-link-mobile"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-zinc-300"
                  >
                    <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
                  </Link>
                  <button
                    data-testid="nav-logout-mobile"
                    onClick={() => { logout(); setOpen(false); }}
                    className="text-sm uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              ) : (
                <div className="flex gap-6">
                  {navLink("/login", "Sign in", "nav-login-mobile")}
                  {navLink("/signup", "Create account", "nav-signup-mobile")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
